// Sostanze dell'Allegato II Reg. UE 1169/2011, in italiano, ciascuna agganciata
// al codice della categoria `Allergen` già seminata a database
// (backend/prisma/seed-allergens.ts — 14 categorie, EU_ALLERGENS).
//
// Perché questo file esiste: l'art. 21 §1 lett. b Reg. UE 1169/2011 impone di
// dichiarare la SOSTANZA («mandorle»), non la categoria («frutta a guscio»).
// docs/etichettatura-alimentare-riferimento.md §4, nota "Denominazione
// specifica": «va indicato il nome preciso della sostanza dell'Allegato II —
// mandorle, nocciole, noci, non "frutta a guscio"; il cereale specifico, non
// "cereali contenenti glutine"». Dove il testo del piano e questo riferimento
// coincidevano non c'è stata divergenza da segnalare.
//
// I `synonyms` sono le forme che un cuoco scrive davvero in un ingrediente o
// in una ricetta (es. "frumento", "grano tenero", "solfiti", "SO2") e servono
// al matching testuale a valle (import IA, evidenziazione in etichetta). Non
// sono un elenco esaustivo di ogni possibile grafia.

/** Codici delle 14 categorie `Allergen` seminate da backend/prisma/seed-allergens.ts. */
export const EU_ALLERGEN_CATEGORY_CODES = [
  'GLUTEN',
  'CRUSTACEANS',
  'EGGS',
  'FISH',
  'PEANUTS',
  'SOY',
  'MILK',
  'NUTS',
  'CELERY',
  'MUSTARD',
  'SESAME',
  'SULPHITES',
  'LUPIN',
  'MOLLUSCS',
] as const;

export type AllergenCategoryCode = (typeof EU_ALLERGEN_CATEGORY_CODES)[number];

export interface AllergenSubstance {
  /** Chiave stabile, es. 'almond', 'wheat'. Riferimento interno a questa sostanza. */
  id: string;
  /** Codice della categoria Allergen a cui la sostanza appartiene. */
  categoryCode: AllergenCategoryCode;
  /** Nome canonico della sostanza in italiano, come va scritto in etichetta. */
  name: string;
  /** Forme testuali equivalenti usate nel linguaggio corrente di cucina/magazzino. */
  synonyms: readonly string[];
  /** Ordine di visualizzazione stabile all'interno della categoria. */
  sortOrder: number;
}

export const ALLERGEN_SUBSTANCES: readonly AllergenSubstance[] = [
  // --- Allegato II punto 1: cereali contenenti glutine -----------------------
  // Testo dell'Allegato: "grano, segale, orzo, avena, farro, kamut o i loro
  // ceppi ibridati". Il Kamut® è di per sé un ceppo di grano khorasan; un
  // eventuale ibrido (es. triticale, grano x segale) si dichiara con il nome
  // del cereale dominante nella ricetta — non è una sostanza separata.
  {
    id: 'wheat',
    categoryCode: 'GLUTEN',
    name: 'Grano',
    synonyms: [
      'frumento',
      'grano tenero',
      'grano duro',
      'semola di grano duro',
      'farina di grano',
      'farina 00',
      'farina 0',
    ],
    sortOrder: 1,
  },
  {
    id: 'rye',
    categoryCode: 'GLUTEN',
    name: 'Segale',
    synonyms: ['farina di segale'],
    sortOrder: 2,
  },
  {
    id: 'barley',
    categoryCode: 'GLUTEN',
    name: 'Orzo',
    synonyms: ['farina di orzo', "malto d'orzo", 'malto di orzo'],
    sortOrder: 3,
  },
  {
    id: 'oats',
    categoryCode: 'GLUTEN',
    name: 'Avena',
    synonyms: ['farina di avena', "fiocchi d'avena", 'fiocchi di avena'],
    sortOrder: 4,
  },
  {
    id: 'spelt',
    categoryCode: 'GLUTEN',
    name: 'Farro',
    synonyms: ['farina di farro', 'spelta'],
    sortOrder: 5,
  },
  {
    id: 'khorasan-wheat',
    categoryCode: 'GLUTEN',
    name: 'Kamut',
    synonyms: ['grano khorasan', 'grano kamut'],
    sortOrder: 6,
  },

  // --- Allegato II punto 8: frutta a guscio -----------------------------------
  {
    id: 'almond',
    categoryCode: 'NUTS',
    name: 'Mandorle',
    synonyms: ['mandorla', 'farina di mandorle', 'granella di mandorle', 'pasta di mandorle'],
    sortOrder: 1,
  },
  {
    id: 'hazelnut',
    categoryCode: 'NUTS',
    name: 'Nocciole',
    synonyms: ['nocciola', 'granella di nocciole', 'pasta di nocciole', 'farina di nocciole'],
    sortOrder: 2,
  },
  {
    id: 'walnut',
    categoryCode: 'NUTS',
    name: 'Noci',
    synonyms: ['noce', 'gherigli di noce'],
    sortOrder: 3,
  },
  {
    id: 'cashew',
    categoryCode: 'NUTS',
    name: 'Anacardi',
    synonyms: ['anacardo', 'noci di acagiù', 'acagiù'],
    sortOrder: 4,
  },
  {
    id: 'pecan',
    categoryCode: 'NUTS',
    name: 'Noci di pecan',
    synonyms: ['pecan', 'noce di pecan'],
    sortOrder: 5,
  },
  {
    id: 'brazil-nut',
    categoryCode: 'NUTS',
    name: 'Noci del Brasile',
    synonyms: ['noce del brasile', 'noci brasiliane'],
    sortOrder: 6,
  },
  {
    id: 'pistachio',
    categoryCode: 'NUTS',
    name: 'Pistacchi',
    synonyms: ['pistacchio', 'granella di pistacchi', 'pasta di pistacchio'],
    sortOrder: 7,
  },
  {
    id: 'macadamia',
    categoryCode: 'NUTS',
    name: 'Noci macadamia',
    synonyms: ['macadamia', 'noci del queensland', 'noce macadamia'],
    sortOrder: 8,
  },

  // --- Categorie mono-sostanza (Allegato II punti 2-7, 9-14) ------------------
  // Rappresentate comunque come sostanza (uguale forma di GLUTEN/NUTS) così
  // la pipeline a valle (evidenza allergeni, matching testuale) è uniforme.
  {
    id: 'crustaceans',
    categoryCode: 'CRUSTACEANS',
    name: 'Crostacei',
    synonyms: [
      'gamberi',
      'gamberetti',
      'scampi',
      'granchio',
      'granchi',
      'astice',
      'aragosta',
      'canocchie',
    ],
    sortOrder: 1,
  },
  {
    id: 'eggs',
    categoryCode: 'EGGS',
    name: 'Uova',
    synonyms: ['uovo', 'uovo di gallina', 'albume', "chiara d'uovo", 'tuorlo'],
    sortOrder: 1,
  },
  {
    id: 'fish',
    categoryCode: 'FISH',
    name: 'Pesce',
    synonyms: ['pesci', 'colla di pesce', 'gelatina di pesce', 'brodo di pesce', 'pesce azzurro'],
    sortOrder: 1,
  },
  {
    id: 'peanuts',
    categoryCode: 'PEANUTS',
    name: 'Arachidi',
    synonyms: [
      'arachide',
      'noccioline',
      'nocciolina americana',
      'burro di arachidi',
      'olio di arachidi',
    ],
    sortOrder: 1,
  },
  {
    id: 'soy',
    categoryCode: 'SOY',
    name: 'Soia',
    synonyms: [
      'semi di soia',
      'farina di soia',
      'lecitina di soia',
      'salsa di soia',
      'tofu',
      'proteine di soia',
    ],
    sortOrder: 1,
  },
  {
    id: 'milk',
    categoryCode: 'MILK',
    name: 'Latte',
    synonyms: [
      'latte vaccino',
      'lattosio',
      'panna',
      'crema di latte',
      'burro',
      'siero di latte',
      'latte in polvere',
      'proteine del latte',
      'caseina',
      'caseinati',
    ],
    sortOrder: 1,
  },
  {
    id: 'celery',
    categoryCode: 'CELERY',
    name: 'Sedano',
    synonyms: ['sedano rapa', 'costa di sedano', 'sale di sedano'],
    sortOrder: 1,
  },
  {
    id: 'mustard',
    categoryCode: 'MUSTARD',
    name: 'Senape',
    synonyms: ['senape in polvere', 'semi di senape', 'farina di senape'],
    sortOrder: 1,
  },
  {
    id: 'sesame',
    categoryCode: 'SESAME',
    name: 'Semi di sesamo',
    synonyms: ['sesamo', 'tahin', 'tahini', 'olio di sesamo'],
    sortOrder: 1,
  },
  {
    id: 'sulphites',
    categoryCode: 'SULPHITES',
    name: 'Anidride solforosa e solfiti',
    synonyms: [
      'solfiti',
      'anidride solforosa',
      'so2',
      'metabisolfito',
      'metabisolfito di potassio',
      'metabisolfito di sodio',
      'e220',
      'e223',
      'e224',
      'e228',
    ],
    sortOrder: 1,
  },
  {
    id: 'lupin',
    categoryCode: 'LUPIN',
    name: 'Lupini',
    synonyms: ['lupino', 'farina di lupini'],
    sortOrder: 1,
  },
  {
    id: 'molluscs',
    categoryCode: 'MOLLUSCS',
    name: 'Molluschi',
    synonyms: [
      'cozze',
      'vongole',
      'calamari',
      'calamaro',
      'polpo',
      'seppie',
      'seppia',
      'lumache di mare',
    ],
    sortOrder: 1,
  },
];

export function getAllergenSubstanceById(id: string): AllergenSubstance | undefined {
  return ALLERGEN_SUBSTANCES.find((substance) => substance.id === id);
}

export function getAllergenSubstancesByCategory(
  categoryCode: AllergenCategoryCode,
): readonly AllergenSubstance[] {
  return ALLERGEN_SUBSTANCES.filter((substance) => substance.categoryCode === categoryCode);
}
